import {
  breederLevelSelectionOptions,
  littersByFemaleOptions,
  motherRestBetweenLittersOptions,
  pedigreeSelectOptions,
  useRouter,
} from '@application/utils';
import { CountryCode } from '@gql/graphql';
import {
  InfoBox,
  InputField,
  InputLabel,
  InputRadioField,
  InputSelectField,
  InputWrapper,
  MtImagesArray,
  MtLayoutTitleWrapper,
  SectionTitle,
} from '@presentation/components';
import type { MtProfileBreederPageProps, MtProfileBreederValues } from '@presentation/pages';
import type { PedigreeType } from '@application/types';
import { useFormikContext } from 'formik';
import type { FC } from 'react';
import { useMemo } from 'react';

export const IMAGES_AMOUNT = 6;

export const PrivateProfile: FC<MtProfileBreederPageProps> = (props) => {
  const { options, mode } = props;
  const { values } = useFormikContext<MtProfileBreederValues>();
  const { translate } = useRouter();

  const breederTypeWarning = translate(
    `mt.breeder.onboarding.breederType.warning.${values.country}`,
    { defaultValue: '' },
  );

  const agreementNumberIsRequired = useMemo(() => {
    switch (values.country) {
      case CountryCode.NL:
        return true;
      case CountryCode.BE:
        return true;
      case CountryCode.FR:
        return values?.level === '3';
      case CountryCode.CH:
        return values?.level !== '4';
      default: // DE + CH
        return false;
    }
  }, [values.level, values.country]);

  const hideAgreementNumberField = useMemo(() => {
    return [CountryCode.DE, CountryCode.CH].includes(values?.country as CountryCode);
  }, [values.country]);

  const hideBreederLevelField = useMemo(() => {
    return [CountryCode.CH].includes(values?.country as CountryCode);
  }, [values.country]);

  return (
    <>
      <InputWrapper>
        <InputLabel required>{translate('common.inputs.breederPictures.label')}</InputLabel>
        <MtImagesArray values={values} mode={mode} />
      </InputWrapper>

      <InputWrapper split>
        <InputLabel required>{translate('common.inputs.breederName.label')}</InputLabel>
        <InputField name="displayName" disabled={mode === 'view'} />
      </InputWrapper>

      <InputWrapper split last>
        <InputLabel>{translate('common.inputs.breedingName.label')}</InputLabel>
        <InputField name="breedingName" disabled={mode === 'view'} />
      </InputWrapper>

      <InputWrapper split>
        <InputWrapper>
          <InputLabel required>{translate('common.inputs.raisedBreeds.label')}</InputLabel>
          <InputSelectField
            disabled={mode === 'view'}
            name="dogBreedsIds"
            multiple
            dismissOnClick={false}
            options={options.breeds}
          />
        </InputWrapper>
        {!hideAgreementNumberField ? (
          <InputWrapper>
            <InputLabel required={agreementNumberIsRequired}>
              {translate(`common.inputs.agreementNumber.label.${values.country}`)}
            </InputLabel>
            <InputField disabled={mode === 'view'} name="agreementNumber" />
          </InputWrapper>
        ) : null}

        <InputWrapper>
          <InputLabel> {translate('common.inputs.websiteurl.website')}</InputLabel>
          <InputField disabled={mode === 'view'} name="websiteUrl" />
        </InputWrapper>

        <InputWrapper>
          <InputLabel>{translate('common.inputs.websiteurl.facebook')}</InputLabel>
          <InputField disabled={mode === 'view'} name="facebookUrl" />
        </InputWrapper>

        <InputWrapper>
          <InputLabel required>
            {translate('common.inputs.veterinaryInfo.label').replaceAll('<br/>', '')}
          </InputLabel>
          <InputField textarea name="veterinaryInfo" disabled={mode === 'view'} />
        </InputWrapper>

        <InputWrapper>
          <InputLabel required>
            {translate('common.inputs.diseaseTesting.label').replaceAll('<br/>', '')}
          </InputLabel>
          <InputField textarea name="medicalTestsPerformed" disabled={mode === 'view'} />
        </InputWrapper>
      </InputWrapper>

      <InputWrapper split>
        {!hideBreederLevelField ? (
          <InputWrapper>
            <InputLabel required>{translate('common.inputs.breederType.label')}</InputLabel>
            {breederTypeWarning && (
              <InfoBox
                dangerouslySetInnerHTML={{
                  __html: breederTypeWarning,
                }}
              />
            )}
            <InputSelectField
              disabled={mode === 'view'}
              noEmpty
              name="level"
              options={breederLevelSelectionOptions(translate, values.country)}
            />
          </InputWrapper>
        ) : null}
        <InputWrapper>
          <InputLabel required>{translate('common.inputs.pedigree.label')}</InputLabel>
          <InputSelectField<PedigreeType>
            useSearch={false}
            disabled={mode === 'view'}
            name="dogsPedigree"
            options={pedigreeSelectOptions(translate, {
              showText: true,
              showIcons: false,
              value: values.dogsPedigree,
            })}
            required
          />
        </InputWrapper>

        {values.dogsPedigree != null && values.dogsPedigree !== 'FCI' && (
          <InputWrapper>
            <InputLabel required>{translate('common.inputs.otherPedigree.label')}</InputLabel>
            <InputField
              disabled={mode === 'view'}
              name="dogsPedigree"
              placeholder={translate('common.inputs.pedigreeNumber.placeholder')}
            />
          </InputWrapper>
        )}
        <InputWrapper>
          <InputLabel required>{translate('common.inputs.femaleBreedersCount.label')}</InputLabel>
          <InputField
            type="number"
            name="femaleBreedersCount"
            min={0}
            required
            inputMode="numeric"
            disabled={mode === 'view'}
          />
        </InputWrapper>
        <InputWrapper>
          <InputLabel required>{translate('common.inputs.breedFemales.label')}</InputLabel>
          <InputSelectField<string>
            useSearch={false}
            disabled={mode === 'view'}
            name="motherRestBetweenLitters"
            options={motherRestBetweenLittersOptions(translate)}
            placeholder={translate('common.inputs.breedFemales.placeholder')}
            required
          />
        </InputWrapper>

        <InputWrapper>
          <InputLabel required>{translate('common.inputs.littersByFemale.label')}</InputLabel>
          <InputSelectField<string>
            useSearch={false}
            disabled={mode === 'view'}
            name="littersByFemale"
            options={littersByFemaleOptions}
            placeholder={translate('common.inputs.littersByFemale.placeholder')}
            required
          />
        </InputWrapper>
      </InputWrapper>

      {/* Translated fields */}
      <MtLayoutTitleWrapper>
        <SectionTitle>
          {translate('mt.breeder.onboarding.title.information').replace(
            '$lng',
            translate(`enum.language.${values.language?.toUpperCase()}`),
          )}
        </SectionTitle>
      </MtLayoutTitleWrapper>

      {!(mode === 'view' && (values.translations ?? []).length <= 1) && (
        <InputWrapper>
          <InputRadioField
            options={(values?.translations ?? []).map((translation) => ({
              value: translation,
              label: translate(`enum.language.${translation.toUpperCase()}`),
            }))}
            name="language"
            grow
          />
        </InputWrapper>
      )}

      <InputWrapper>
        <InfoBox>{translate('mt.breeder.useChatInfo')}</InfoBox>
        <InputLabel required>{translate('common.inputs.description.label')}</InputLabel>
        <InputField
          name={`translated.${values.language}.description`}
          disabled={mode === 'view'}
          textarea
        />
      </InputWrapper>

      <InputWrapper>
        <InputLabel required>{translate('common.inputs.breedsLiving.label')}</InputLabel>
        <InputField
          textarea
          name={`translated.${values.language}.dogsLivingPlace`}
          disabled={mode === 'view'}
        />
      </InputWrapper>

      <InputWrapper>
        <InputLabel required>{translate('common.inputs.breedsSelection.label')}</InputLabel>
        <InputField
          disabled={mode === 'view'}
          textarea
          name={`translated.${values.language}.breederDogsSelection`}
        />
      </InputWrapper>

      <InputWrapper>
        <InputLabel required>{translate('common.inputs.placeOfGrowth.label')}</InputLabel>
        <InputField
          disabled={mode === 'view'}
          textarea
          name={`translated.${values.language}.puppiesLivingPlace`}
        />
      </InputWrapper>

      <InputWrapper>
        <InputLabel required>{translate('common.inputs.puppetWork.label')}</InputLabel>
        <InputField
          textarea
          name={`translated.${values.language}.puppiesTraining`}
          disabled={mode === 'view'}
        />
      </InputWrapper>
    </>
  );
};
